import { useState, type SubmitEvent } from 'react';
import { Building2, Check, Eye, EyeOff, LocateFixed, Mail, UserPlus, UserRound, type LucideIcon } from 'lucide-react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { regionOptions, serviceOptions } from '../catalog';
import { localizedApiError } from '../i18n/apiErrors';
import { useI18n } from '../i18n/I18nContext';
import type { TranslationKey } from '../i18n/translations';
import type { RegistrationAccountType, RegistrationPayload } from '../types';

type Coordinates = { lat: number; lng: number };

const accountTypes = [
  { value: 'professional', icon: UserRound, labelKey: 'register.type.professional', hintKey: 'register.type.professionalHint' },
  { value: 'organization', icon: Building2, labelKey: 'register.type.organization', hintKey: 'register.type.organizationHint' }
] satisfies Array<{ value: RegistrationAccountType; icon: LucideIcon; labelKey: TranslationKey; hintKey: TranslationKey }>;

// Sprachen werden wie in bestehenden Profilen mit ihrem Eigennamen gespeichert.
const languageOptions = ['Português', 'English', 'Deutsch', 'Español', 'Français', 'Italiano'];
const organizationTypeSuggestions = ['Hostel', 'Hotel', 'Pousada', 'Kiteschule', 'Spa', 'Restaurant'];

function isAccountType(value: string | null): value is RegistrationAccountType {
  return value === 'professional' || value === 'organization';
}

function toggleValue(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
}

/** Ergänzt fehlendes https://, damit «example.com» nicht an der URL-Prüfung scheitert. */
function normalizeWebsite(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/** Registrierung als Profi oder Organisation; der gewählte Typ steht in der URL (?type=…). */
export function RegisterPage() {
  const { register, status } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const accountType = isAccountType(typeParam) ? typeParam : null;

  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [services, setServices] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [organizationName, setOrganizationName] = useState('');
  const [organizationType, setOrganizationType] = useState('');
  const [locationName, setLocationName] = useState('');
  const [position, setPosition] = useState<Coordinates | null>(null);
  const [locating, setLocating] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (status === 'authenticated') return <Navigate to="/" replace />;

  function selectType(type: RegistrationAccountType): void {
    setSearchParams({ type });
    setError(null);
  }

  /** Eine übernommene Browserposition hat Vorrang, sonst gelten Name und Koordinaten des bekannten Orts. */
  function resolveLocation(): ({ locationName: string } & Coordinates) | null {
    const typedName = locationName.trim();
    const region = regionOptions.find((entry) => entry.label.toLowerCase() === typedName.toLowerCase());
    if (position) return { locationName: region?.label ?? typedName, ...position };
    return region ? { locationName: region.label, lat: region.lat, lng: region.lng } : null;
  }

  function useCurrentPosition(): void {
    if (!navigator.geolocation) {
      setError(t('search.locationUnavailable'));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition((result) => {
      setPosition({ lat: result.coords.latitude, lng: result.coords.longitude });
      setError(null);
      setLocating(false);
    }, () => {
      setError(t('search.locationFailed'));
      setLocating(false);
    }, { enableHighAccuracy: true, maximumAge: 10 * 60 * 1000, timeout: 8000 });
  }

  async function submit(event: SubmitEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!accountType) return;
    if (accountType === 'professional' && services.length === 0) {
      setError(t('register.servicesRequired'));
      return;
    }
    const location = resolveLocation();
    if (!location) {
      setError(t('register.locationRequired'));
      return;
    }

    const base = {
      email: email.trim(),
      password,
      name: name.trim(),
      ...location,
      websiteUrl: normalizeWebsite(websiteUrl)
    };
    const payload: RegistrationPayload = accountType === 'professional'
      ? { ...base, accountType, title: title.trim(), services, languages }
      : { ...base, accountType, organizationName: organizationName.trim(), organizationType: organizationType.trim() };

    setError(null);
    setLoading(true);
    try {
      await register(payload);
      navigate('/', { replace: true });
    } catch (reason) {
      setError(localizedApiError(reason, t, 'error.registrationUnavailable'));
    } finally {
      setLoading(false);
    }
  }

  const optional = <span className="field-optional">({t('register.optional')})</span>;

  return (
    <main className="gate login-page">
      <section className="gate-panel register-panel">
        <div className="gate-mark"><UserPlus size={24} /></div>
        <h1>{t('register.title')}</h1>
        <p className="form-intro">{t('register.intro')}</p>

        <fieldset className="account-type-picker">
          <legend>{t('register.typeLegend')}</legend>
          <div className="account-type-grid">
            {accountTypes.map(({ value, icon: Icon, labelKey, hintKey }) => (
              <label key={value} className={`account-type-card${accountType === value ? ' is-selected' : ''}`}>
                <input type="radio" name="accountType" value={value} checked={accountType === value} onChange={() => selectType(value)} />
                <Icon size={22} />
                <strong>{t(labelKey)}</strong>
                <span>{t(hintKey)}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {accountType && (
          <form onSubmit={submit} className="gate-form register-form">
            {accountType === 'professional' ? (
              <fieldset className="form-section">
                <legend>{t('register.section.profile')}</legend>
                <label htmlFor="register-name">{t('register.fullName')}</label>
                <input id="register-name" autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} minLength={2} maxLength={120} required />
                <label htmlFor="register-title">{t('register.jobTitle')}</label>
                <input id="register-title" autoComplete="organization-title" placeholder={t('register.jobTitlePlaceholder')} value={title} onChange={(event) => setTitle(event.target.value)} minLength={2} maxLength={120} required />
                <fieldset className="chip-group">
                  <legend>{t('register.services')}</legend>
                  <div className="chip-list">
                    {serviceOptions.map((option) => (
                      <label key={option.value} className="chip">
                        <input type="checkbox" checked={services.includes(option.value)} onChange={() => setServices((current) => toggleValue(current, option.value))} />
                        <span>{t(option.labelKey)}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <fieldset className="chip-group">
                  <legend>{t('register.languages')} {optional}</legend>
                  <div className="chip-list">
                    {languageOptions.map((language) => (
                      <label key={language} className="chip">
                        <input type="checkbox" checked={languages.includes(language)} onChange={() => setLanguages((current) => toggleValue(current, language))} />
                        <span>{language}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </fieldset>
            ) : (
              <fieldset className="form-section">
                <legend>{t('register.section.organization')}</legend>
                <label htmlFor="register-organization">{t('register.organizationName')}</label>
                <input id="register-organization" autoComplete="organization" value={organizationName} onChange={(event) => setOrganizationName(event.target.value)} minLength={2} maxLength={160} required />
                <label htmlFor="register-organization-type">{t('register.organizationType')}</label>
                <input id="register-organization-type" list="organization-type-suggestions" placeholder={t('register.organizationTypePlaceholder')} value={organizationType} onChange={(event) => setOrganizationType(event.target.value)} minLength={2} maxLength={80} required />
                <datalist id="organization-type-suggestions">
                  {organizationTypeSuggestions.map((type) => <option key={type} value={type} />)}
                </datalist>
                <label htmlFor="register-name">{t('register.contactName')}</label>
                <input id="register-name" autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} minLength={2} maxLength={120} required />
              </fieldset>
            )}

            <fieldset className="form-section">
              <legend>{t('register.section.location')}</legend>
              <label htmlFor="register-location">{t('common.locationRegion')}</label>
              <input id="register-location" list="region-suggestions" aria-describedby="register-location-hint" value={locationName} onChange={(event) => setLocationName(event.target.value)} minLength={2} maxLength={160} required />
              <datalist id="region-suggestions">
                {regionOptions.map((region) => <option key={region.id} value={region.label}>{t(region.detailKey)}</option>)}
              </datalist>
              <p id="register-location-hint" className="form-hint">{t('register.locationHint')}</p>
              <div className="location-row">
                <button type="button" className="secondary-button" onClick={useCurrentPosition} disabled={locating}>
                  <LocateFixed size={16} /> {locating ? t('register.locating') : t('register.useCurrentPosition')}
                </button>
                {position && <span className="position-status"><Check size={15} /> {t('register.positionApplied')}</span>}
              </div>
              <label htmlFor="register-website">{t('common.website')} {optional}</label>
              <input id="register-website" inputMode="url" autoComplete="url" placeholder="example.com" value={websiteUrl} onChange={(event) => setWebsiteUrl(event.target.value)} maxLength={300} />
            </fieldset>

            <fieldset className="form-section">
              <legend>{t('register.section.access')}</legend>
              <label htmlFor="register-email">{t('login.email')}</label>
              <div className="input-with-icon"><Mail size={17} /><input id="register-email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} maxLength={254} required /></div>
              <label htmlFor="register-password">{t('login.password')}</label>
              <div className="password-field">
                <input id="register-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" aria-describedby="register-password-hint" value={password} onChange={(event) => setPassword(event.target.value)} minLength={10} maxLength={72} required />
                <button type="button" className="password-toggle" onClick={() => setShowPassword((current) => !current)} aria-pressed={showPassword} aria-label={showPassword ? t('register.hidePassword') : t('register.showPassword')}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p id="register-password-hint" className="form-hint">{t('register.passwordHint')}</p>
            </fieldset>

            {error && <p className="form-error" role="alert">{error}</p>}
            <button type="submit" disabled={loading}>{loading ? t('register.running') : t('register.submit')}</button>
          </form>
        )}

        <p className="auth-switch">{t('register.haveAccount')} <Link to="/login">{t('login.title')}</Link></p>
      </section>
    </main>
  );
}
